import React, { useState } from "react";
import { useLang } from "@/context/LangContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import api, { getErrorDetail } from "@/lib/api";
import { BookOpen, Sparkles, Check, Download } from "lucide-react";
import { API_BASE } from "@/lib/api";

export default function ReadingChallengeSection() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", child_age_band: "primary", consent: true });
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState("idle"); // idle | success | already

  async function submit(e) {
    e.preventDefault();
    if (!form.consent) { toast.error(t.reading.consent); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/leads", {
        name: form.name,
        email: form.email.trim().toLowerCase(),
        child_age_band: form.child_age_band,
        source: "reading_challenge",
        consent: form.consent,
      });
      setState(data?.status === "already_subscribed" ? "already" : "success");
    } catch (err) {
      toast.error(getErrorDetail(err));
    } finally { setLoading(false); }
  }

  return (
    <section id="reading-challenge" data-testid="reading-section" className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 border-t border-[hsl(var(--border))]">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-stretch">
        {/* Left — copy */}
        <div className="md:col-span-7 relative overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))] p-8 md:p-12">
          <div className="absolute -right-12 -top-12 w-56 h-56 rounded-full bg-[hsl(var(--primary))]/30 blur-2xl" />
          <div className="relative">
            <p className="text-xs tracking-[0.25em] text-[hsl(var(--secondary-foreground))]/70">{t.reading.eyebrow}</p>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium mt-3 leading-[1.05]">
              {t.reading.title_a} <span className="text-[hsl(var(--primary))]">{t.reading.title_b}</span>
            </h2>
            <p className="mt-5 max-w-xl text-[hsl(var(--secondary-foreground))]/80">{t.reading.sub}</p>

            <ul className="mt-7 space-y-2.5 max-w-md">
              {t.reading.points.map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-1 text-[hsl(var(--primary))] flex-shrink-0" />
                  <span className="text-sm text-[hsl(var(--secondary-foreground))]/90">{p}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex items-center gap-2 text-xs text-[hsl(var(--secondary-foreground))]/70">
              <BookOpen className="w-4 h-4" />
              <span>30 days · 15 minutes / day · printable PDF</span>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div className="md:col-span-5 rounded-2xl border border-[hsl(var(--border))] bg-white p-8">
          {state === "idle" && (
            <form onSubmit={submit} className="space-y-5" data-testid="reading-form">
              <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[hsl(var(--primary))]">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Free planner</span>
              </div>
              <div>
                <Label htmlFor="rc-name" className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.reading.name}</Label>
                <Input id="rc-name" required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="reading-name" className="mt-2" />
              </div>
              <div>
                <Label htmlFor="rc-email" className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.reading.email}</Label>
                <Input id="rc-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} data-testid="reading-email" className="mt-2" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.reading.age_band}</Label>
                <RadioGroup value={form.child_age_band} onValueChange={(v)=>setForm({...form, child_age_band:v})} className="grid grid-cols-2 gap-2 mt-3" data-testid="reading-age">
                  {[
                    ["primary", t.reading.stages.primary],
                    ["teens", t.reading.stages.teens],
                    ["k12", t.reading.stages.k12],
                    ["ib_alevel_cpe", t.reading.stages.ib_alevel_cpe],
                  ].map(([val, label]) => (
                    <label key={val} className={`flex items-center gap-2 cursor-pointer text-sm px-3 py-2.5 rounded-lg border transition-colors ${form.child_age_band===val ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 text-[hsl(var(--foreground))]' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--foreground))]/30'}`}>
                      <RadioGroupItem value={val} data-testid={`reading-age-${val}`} />
                      <span>{label}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <label className="flex items-start gap-3 text-sm text-[hsl(var(--muted-foreground))] cursor-pointer">
                <Checkbox checked={form.consent} onCheckedChange={(v)=>setForm({...form, consent:!!v})} data-testid="reading-consent" className="mt-0.5" />
                <span>{t.reading.consent}</span>
              </label>
              <Button type="submit" disabled={loading} data-testid="reading-submit" className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-full lift">
                {loading ? "…" : t.reading.submit}
              </Button>
            </form>
          )}

          {state === "success" && (
            <div data-testid="reading-success" className="space-y-5">
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--secondary))]/10 flex items-center justify-center">
                <Check className="w-6 h-6 text-[hsl(var(--secondary))]" />
              </div>
              <h3 className="font-display text-2xl tracking-tight font-medium">{t.reading.success_title}</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{t.reading.download_body}</p>
              <Button
                asChild
                data-testid="reading-download-btn"
                className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-full lift"
              >
                <a
                  href={`${API_BASE}/planner/${form.child_age_band}?email=${encodeURIComponent(form.email.trim().toLowerCase())}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t.reading.download_btn}
                </a>
              </Button>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{t.reading.download_hint}</p>
            </div>
          )}
          {state === "already" && (
            <div data-testid="reading-already" className="space-y-5">
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center">
                <Check className="w-6 h-6 text-[hsl(var(--muted-foreground))]" />
              </div>
              <h3 className="font-display text-2xl tracking-tight font-medium">{t.reading.already_title}</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{t.reading.already_body}</p>
              <Button
                asChild
                variant="outline"
                data-testid="reading-download-again-btn"
                className="w-full rounded-full"
              >
                <a
                  href={`${API_BASE}/planner/${form.child_age_band}?email=${encodeURIComponent(form.email.trim().toLowerCase())}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t.reading.download_again_btn}
                </a>
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
